import { Button } from "primereact/button";
import NewCreateInvoiceItems from "./new-create-invoice-items";
import NewCreateInvoiceTop from "./new-create-invoice-top";
import { useEffect, useState, useCallback } from "react";
import NewInvoicePayment from "./new-invoice-payment";
import '../../assests/css/common/responsive-ui.css'
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import orderServices from '../../services/orders/orderservice';
import { POSModel } from '../../models/orders/order-list-model';
import { Dialog } from "primereact/dialog";
import { NewAddProduct } from "./new-add-product";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { OrderDetailWithBatches, ProductIds } from "../../models/orders/order-list-model";
import PaymentConfirmDialog from "../orders/payment-details-dialog";

const NewCreateInvoice = (props: any) => {
  const [invoiceCheckoutItem, setInvoiceCheckoutItem] = useState<any>(null);
  const [paymentModeId, setPaymentModeId] = useState<number | null>(null);
  const [isPaymentModeSelected, setIsPaymentModeSelected] = useState(false);
  const [paymentModeError, setPaymentModeError] = useState(false);
  const [pointOfSale, setPointOfSale] = useState<number>();
  const [posData, setPOSData] = useState<POSModel[]>([]);
  const [posCodes, setPosCodes] = useState<{ name: string, code: number }[]>([]);
  const [selectedAddr, setSelectedAddr] = useState("");
  const [isInvoiceCreating, setIsInvoiceCreating] = useState(false);
  const invoiceSubmitting = useRef(false);
  const invoiceActive = useRef(true);
  useEffect(() => { invoiceActive.current = true; return () => { invoiceActive.current = false; }; }, []);
  const [addProductVisible, setAddProductVisible] = useState(false);
  const [isPOSSelected, setIsPOSSelected] = useState(false);
  const [showpaymentOptions, setShowpaymentOptions] = useState<boolean>(true);
  const [isOrderDataEmpty, setIsOrderDataEmpty] = useState(true);
  const [hasInsufficientStock, setHasInsufficientStock] = useState(false);
  const [hasUnsubmittedQuantityChanges, setHasUnsubmittedQuantityChanges] = useState(false);
  const toast = useRef<Toast>(null);
  const [isConfirmDialog, setIsConfirmDialog] = useState(false);
  const [rewardData, setRewardData] = useState({ redeemedPoints: 0, finalPayable: 0, redeemMorePoints: false });

  const validateBatches = (orderDetails: any[]) => {
    for (const item of orderDetails) {
      // Skip if quantity is zero
      if (!item.pendingQuantity || item.pendingQuantity === 0) continue;

      // ✅ SKIP EMPTY BOTTLE
      if (item.productID === EMPTY_BOTTLE_PRODUCT_ID) continue;

      if (!item.batches || item.batches.length === 0) {
        return `Please select at least one batch for ${item.productName}`;
      }

      let totalBatchQty = 0;

      for (const batch of item.batches) {
        if (!batch.batchId) {
          return `Please select batch for ${item.productName}`;
        }

        if (!batch.quantity || batch.quantity <= 0) {
          return `Please enter batch quantity for ${item.productName}`;
        }

        totalBatchQty += batch.quantity;
      }

      if (totalBatchQty !== item.pendingQuantity) {
        return `Batch quantity (${totalBatchQty}) does not match item quantity (${item.pendingQuantity}) for ${item.productName}`;
      }

    }

    return null; // ✅ valid
  };

  const handleCreateInvoice = async () => {
    if (invoiceSubmitting.current) return;
    const isZeroPayable = (rewardData.redeemedPoints > 0 && rewardData.finalPayable === 0) || (props.OrderData.payId === 10 && rewardData.finalPayable === 0);

    if (showpaymentOptions && !isPaymentModeSelected && !isZeroPayable) {
      setPaymentModeError(true);
      return;
    }

    const batchError = validateBatches(invoiceCheckoutItem?.orderDetails || []);
    if (batchError) {
      toast.current?.show({
        severity: "error",
        summary: "Batch Required",
        detail: batchError,
        life: 4000
      });
      return;
    }

    try {
      invoiceSubmitting.current = true;
      setIsInvoiceCreating(true);

      // Delivery Assessment can require Start before the first invoice and before product inserts.
      if (props.beforeCreateInvoice && !await props.beforeCreateInvoice()) return;
      if (!invoiceActive.current) return;

      // LOCAL COPY → Do NOT depend on React state
      let updatedOrderDetails = [...invoiceCheckoutItem.orderDetails];

      // 1️⃣ INSERT NEW PRODUCTS FIRST
      const newProducts = updatedOrderDetails.filter(x => x.orderDetailID === 0);

      if (newProducts.length > 0) {
        const payload = newProducts.map(item => ({
          OrderID: invoiceCheckoutItem.orderID,
          ProductID: item.productID,

          OrderStatusID: invoiceCheckoutItem.orderStatusID ?? 2,
          UnitPrice: item.unitPrice,
          Quantity: item.quantity,
          PendingQuantity: item.pendingQuantity,

          TaxableAmount: item.taxableAmount,
          TaxAmount: item.taxAmount,
          DiscountAmount: invoiceCheckoutItem.discountAmount ?? 0,

          GrossAmount: item.grossAmount,

          TrackingID: item.trackingID ?? "1",
          CourierDocketNbr: "",

          ExpectedDeliveryDate: new Date().toISOString(),

          ModifiedBy: invoiceCheckoutItem.userID,
          ModifiedDate: new Date().toISOString(),

          OrderGrossAmount:
            (invoiceCheckoutItem.grossAmount ?? 0) + item.grossAmount,

          OrderTaxableAmount:
            (invoiceCheckoutItem.taxableAmount ?? 0) + item.taxableAmount,

          OrderTaxAmount:
            (invoiceCheckoutItem.taxAmount ?? 0) + item.taxAmount
        }));

        const insertedMap = await orderServices.bulkInsertNewInvoiceProducts(payload);

        updatedOrderDetails = updatedOrderDetails.map(item =>
          item.orderDetailID === 0
            ? { ...item, orderDetailID: insertedMap[item.productID] }
            : item
        );
      }

      // 2️⃣ FINAL OBJECT (DO NOT USE STATE)
      const finalInvoicePayload = {
        ...invoiceCheckoutItem,
        orderDetails: updatedOrderDetails,
        rewardPointsUsed: rewardData.redeemedPoints,
        finalPayableAmount: rewardData.finalPayable,
        redeemMorePoints: rewardData.redeemMorePoints
      };

      // 3️⃣ NOW CREATE INVOICE
      const isZeroPayable = (rewardData.redeemedPoints > 0 && rewardData.finalPayable === 0) || (props.OrderData.payId === 10 && rewardData.finalPayable === 0);
      const finalPaymentModeId = isZeroPayable ? 10 : paymentModeId;

      const created = await props.runCreateInvoice(finalInvoicePayload, finalPaymentModeId);
      if (created === false) return;

      if ((paymentModeId === 5 || paymentModeId === 9) && finalInvoicePayload.paymentAlreadyCharged === false && props.OrderData?.amount > 0) {
        setIsConfirmDialog(true);
      } else {
        props.runHideDialog();
      }
    } catch (err) {
      console.error("Error at Create Invoice:", err);
    } finally {
      invoiceSubmitting.current = false;
      setIsInvoiceCreating(false);
    }
  };

  const handleOrderDataEmptyChange = useCallback((isEmpty: boolean) => {
    setIsOrderDataEmpty(isEmpty);
  }, []);

  const handlePOSChange = useCallback((e: DropdownChangeEvent) => {
    const selectedValue = e.value;
    setPointOfSale(selectedValue);
    setIsPOSSelected(!!selectedValue);

    setInvoiceCheckoutItem((prevState: any) => ({
      ...prevState,
      posID: selectedValue
    }));

    const filteredPOS = posData.filter(item => item.posID === selectedValue);
    const addressData = filteredPOS.map(item => {
      return [
        item.address1,
        item.address2,
        item.landmark,
        item.cityName,
        item.stateName,
        item.zipcode ? `Pin: ${item.zipcode}` : ''
      ].filter(Boolean).join(", ");
    });
    setSelectedAddr(addressData[0] || "");
  }, [posData]);

  const getALLPOS = useCallback(() => {
    orderServices.getPOS().then((response: POSModel[]) => {
      setPOSData(response);
      const posCodes = response.map(item => ({ name: item.posCode, code: item.posID }));
      setPosCodes(posCodes);
      if (response.length === 1) {
        const singlePOS = response[0];
        setPointOfSale(singlePOS.posID);
        setIsPOSSelected(true);

        setInvoiceCheckoutItem((prevState: any) => ({
          ...prevState,
          posID: singlePOS.posID
        }));

        let addressData =
          (singlePOS.address1 ? singlePOS.address1 + ", " : "") +
          (singlePOS.address2 ? singlePOS.address2 + ", " : "") +
          (singlePOS.landmark ? singlePOS.landmark + ", " : "") +
          (singlePOS.cityName ? singlePOS.cityName + ", " : "") +
          (singlePOS.stateName ? singlePOS.stateName + ", " : "") +
          (singlePOS.zipcode ? "Pin: " + singlePOS.zipcode : "");

        setSelectedAddr(addressData.trim());
      }
    }).catch(error => {
      console.error("Error fetching POS:", error);
    });
  }, []);

  const updateItem = useCallback((newItems: any, taxableAmt: number, taxAmt: number, disAmt: number) => {
    setInvoiceCheckoutItem((prevState: any) => {
      // Ensure batch details are included in orderDetails
      const updatedOrderDetails = newItems.map((item: any) => ({
        ...item,
        batches: item.batches || []
      }));

      return {
        ...prevState,
        orderDetails: updatedOrderDetails,
        taxableAmount: Math.round(taxableAmt * 100) / 100,
        taxAmount: Math.round(taxAmt * 100) / 100,
        discountAmount: disAmt
      };
    });
  }, []);

  const updateItemAfterAdd = (newState: any) => {
    const uniqueOrderDetails = [] as any[];

    const map = new Set<number>();

    for (const item of newState.orderDetails) {
      if (!map.has(item.productID)) {
        map.add(item.productID);
        uniqueOrderDetails.push(item);
      }
    }

    setInvoiceCheckoutItem((prev: any) => ({
      ...prev,
      orderDetails: uniqueOrderDetails
    }));
  };


  const handlePaymentModeChange = useCallback((id: number | null) => {
    setPaymentModeId(id);
    setIsPaymentModeSelected(id !== null);
    setPaymentModeError(false);
  }, []);

  useEffect(() => {
    const initialData = {
      ...props.OrderData,
      orderDetails: props.OrderData.orderDetails || [],
      address: {
        address1: props.OrderData.address1,
        address2: props.OrderData.address2,
        landmark: props.OrderData.landmark,
        cityName: props.OrderData.cityName,
        stateName: props.OrderData.stateName,
        zipcode: props.OrderData.zipcode
      }
    };
    setInvoiceCheckoutItem(initialData);

    if (props.OrderData.paymentAlreadyCharged == true) {
      setShowpaymentOptions(false);
      setPaymentModeId(props.OrderData.payId);
      setIsPaymentModeSelected(true);
    } else if (props.OrderData.paymentMode === "Cash" || props.OrderData.payId === 11) {
      setPaymentModeId(11);
      setIsPaymentModeSelected(true);
    }
    getALLPOS();
  }, [props.OrderData, getALLPOS]);


  const handleInsufficientStock = useCallback((isInsufficient: boolean) => {
    setHasInsufficientStock(isInsufficient);
  }, []);

  const EMPTY_BOTTLE_PRODUCT_ID = ProductIds.EMPTY_BOTTLE;

  const isBatchValid = (orderDetails: OrderDetailWithBatches[] = []) => {
    for (const item of orderDetails) {
      if (!item.pendingQuantity || item.pendingQuantity === 0) continue;

      // ✅ SKIP EMPTY BOTTLE
      if (item.productID === EMPTY_BOTTLE_PRODUCT_ID) continue;
      // ❌ No batch selected
      if (!item.batches || item.batches.length === 0) {
        return false;
      }

      let totalQty = 0;

      for (const batch of item.batches) {
        // ❌ batchId missing
        if (!batch.batchId) {
          return false;
        }

        // ❌ invalid quantity
        if (!batch.quantity || batch.quantity <= 0) {
          return false;
        }

        totalQty += batch.quantity;
      }

      // ❌ quantity mismatch
      if (totalQty !== item.pendingQuantity) {
        return false;
      }
    }

    return true; // ✅ everything valid
  };

  const isBatchSelectionValid = isBatchValid(
    invoiceCheckoutItem?.orderDetails || []
  );

  const hideConfirmDialog = () => {
    setIsConfirmDialog(false);
    props.runHideDialog();
  };

  const isZeroPayable = (rewardData.redeemedPoints > 0 && rewardData.finalPayable === 0) || (props.OrderData.payId === 10 && rewardData.finalPayable === 0);

  const isGenerateDisabled = !isPOSSelected || isInvoiceCreating || isOrderDataEmpty || (!isPaymentModeSelected && !isZeroPayable) || hasInsufficientStock || hasUnsubmittedQuantityChanges || !isBatchSelectionValid || hasUnsubmittedQuantityChanges;

  return (
    <>
      <Toast ref={toast} />
      <div className="flex flex-column border-1 surface-border box-shadow-none m-0">
        <NewCreateInvoiceTop orderData={props.OrderData} />
        <div className="flex pl-3 mb-3 col-12 sm:col-12 lg:col-12 border-round">
          <p className="pos-text mt-3 font-bold">Point Of Sale: </p> <span className="required">*</span>
          <div className="ml-4 lg:col-3">
            <Dropdown
              options={posCodes}
              optionLabel="name"
              optionValue="code"
              value={pointOfSale}
              onChange={handlePOSChange}
              placeholder="Select POS"
              style={{ width: "100%" }}
              required
              className={!isPOSSelected && pointOfSale === undefined ? "p-invalid" : ""}
            />
            {!isPOSSelected && pointOfSale === undefined && (
              <small className="p-error">POS is required</small>
            )}
          </div>
          <p className="ml-4 mt-2">{selectedAddr}</p>
        </div>

        {showpaymentOptions && props.OrderData.paymentAlreadyCharged === false && !isZeroPayable && (
          <>
            {!(props.OrderData.paymentMode === "Cash" || props.OrderData.payId === 11) && (
              <div className="pl-3 mb-3">
                <div className="flex align-items-center">
                  <p className="pos-text mt-3 font-bold">Select Payment Mode: </p>
                  <span className="required">*</span>
                </div>
                <NewInvoicePayment updatePaymentId={handlePaymentModeChange} />
                {paymentModeError && (
                  <small className="p-error">Payment mode is required</small>
                )}
                <br />
              </div>
            )}
            <div>
              <div className="ml-3">
                <Button label="Add Product" onClick={() => setAddProductVisible(!props.OrderData.paymentAlreadyCharged)} />
              </div>
              <Dialog
                header="Add Product"
                visible={addProductVisible}
                style={{ width: "67vw" }}
                onHide={() => setAddProductVisible(false)}
                className="addProduct-dialog"
              >
                <NewAddProduct
                  orderedItems={invoiceCheckoutItem}
                  updateItems={updateItemAfterAdd}
                />
              </Dialog>
            </div>
          </>
        )}

        {showpaymentOptions && props.OrderData.paymentAlreadyCharged === false && isZeroPayable && (
          <div className="mb-3">
            <div className="flex align-items-center pl-3 mb-2">
              <p className="pos-text mt-3 font-bold text-green-600">
                <i className="pi pi-check-circle mr-2"></i>
                Full amount covered by Reward Points
              </p>
            </div>
            <div className="ml-3">
              <Button label="Add Product" onClick={() => setAddProductVisible(!props.OrderData.paymentAlreadyCharged)} />
            </div>
            <Dialog
              header="Add Product"
              visible={addProductVisible}
              style={{ width: "67vw" }}
              onHide={() => setAddProductVisible(false)}
              className="addProduct-dialog"
            >
              <NewAddProduct
                orderedItems={invoiceCheckoutItem}
                updateItems={updateItemAfterAdd}
              />
            </Dialog>
          </div>
        )}

        <NewCreateInvoiceItems
          // key={JSON.stringify(invoiceCheckoutItem?.orderDetail)}
          orderedItems={invoiceCheckoutItem}
          updateItems={updateItem}
          onOrderDataEmptyChange={handleOrderDataEmptyChange}
          onInsufficientStockChange={handleInsufficientStock}
          onQuantityChangePending={setHasUnsubmittedQuantityChanges}
          paymentAlreadyCharged={props.OrderData.paymentAlreadyCharged}
          onRewardChange={setRewardData}
        />
      </div>

      <div className="flex justify-content-end flex-wrap mt-2">
        <Button
          label="Close"
          className="border-round mt-2 ml-2 responsive-button"
          onClick={props.runHideDialog}
          style={{ width: "20%", textAlign: 'center' }}
          severity="warning"
        />
        <Button
          label={isInvoiceCreating ? "Processing..." : "Generate Invoice"}
          loading={isInvoiceCreating}
          className="border-round mt-2 ml-2 responsive-button"
          onClick={handleCreateInvoice}
          style={{ width: "20%", textAlign: 'center' }}
          severity="success"
          disabled={isGenerateDisabled}
        />
      </div>
      {
        (paymentModeId === 5 || paymentModeId === 9) && props.OrderData?.amount > 0 ?
          <PaymentConfirmDialog
            showConfirmDialog={isConfirmDialog}
            hideConfirmDialog={hideConfirmDialog}
            orderId={props.OrderData.orderID}
            PlutusTransactionReferenceId={props.OrderData.plutusTransactionReferenceId}
            orderDetails={invoiceCheckoutItem}
            paymentModeType={props.OrderData?.paymentMode}
            amount={props.OrderData?.amount}
          /> : ""
      }
    </>
  );
};

export default NewCreateInvoice;
